<script>
	import Node from "./Node.svelte";
	import {fsType} from "./enums";
	import {preparePost} from "../../common/util/util";
	import { onMount } from 'svelte';

	const rootName = "Home";
	const appPrefix = window.appPrefix || '/projects';
	const readonly = !document.getElementById('project-form')?.dataset.autosaveUrl;

	let documents = null;
	let nbNodes = 0;
	let projectImage = null;
	let loading = true;
	let error = null;
	let projectId = null;

	function getProjectId() {
		// Match pattern like /projects/project/123/ or /project/123/
		const match = window.location.pathname.match(/\/project\/(\d+)/);
		return match && match[1] ? parseInt(match[1]) : null;
	}

	function newName(fileName, parent) {
		const names = new Set();
		if (parent && parent.children) {
			parent.children.forEach(node => names.add(node.name));
		}
		let index = 2;
		const splited = fileName.split('.');
		let initialName = splited[0];
		let finalName = fileName;
		while (names.has(finalName)) finalName = initialName + ' (' + (index++).toString() + ')' +  (splited.length > 1 ? '.' + splited[1] : '');
		return finalName;
	}

	function newNode(nodeName, parent, nodeType, id, confidential, isMainDoc) {
		const node = {
			name: newName(nodeName, parent),
			children: [],
			parent: parent,
			type: nodeType,
			confidential: confidential || false,
			isMainDoc: isMainDoc || false,
			number: nbNodes++ // index for svelte bookkeeping
		};
		if (id) node.id = id;
		return node;
	}

	function createDirTree(docs) {
		const tree = newNode(rootName, null, fsType.FOLDER);
		docs.forEach(doc => {
			let curNode = tree;
			doc.path.forEach( dir  => {
				const childIndex = curNode.children.findIndex(c => c.name == dir);
				if (childIndex != -1) curNode = curNode.children[childIndex];
				else {
					curNode.children.push(newNode(dir, curNode, fsType.FOLDER));
					curNode = curNode.children[curNode.children.length -1];
				}
			});
			curNode.children.unshift(newNode(doc.name, curNode, fsType.FILE, doc.id, doc.confidential, doc.is_main));
		});
		return tree;
	}

	function fetchDocuments() {
		projectId = getProjectId();

		// Only fetch documents if we have a valid project ID
		if (projectId) {
			fetch(`${appPrefix}/get_documents/?proj_id=${projectId}`)
				.then(res => {
					if (!res.ok) {
						throw new Error(`HTTP ${res.status}: ${res.statusText}`);
					}
					return res.json();
				})
				.then(response => {
					documents = createDirTree(response.documents || []);
					projectImage = response.project_image;
					loading = false;
				})
				.catch(err => {
					console.error('Error fetching documents:', err);
					error = err.message;
					documents = createDirTree([]);
					loading = false;
				});
		} else {
			// No project ID, initialize empty tree
			documents = createDirTree([]);
			loading = false;
		}
	}

	// Fetch documents on mount
	onMount(() => {
		fetchDocuments();
	});

	function getAffectedLeafs(currentNode){
		const leafs = [];
		const traversedNames = [currentNode.name];
		const traverse = (node) => {
			node.children.forEach(child => {
				traversedNames.push(child.name);
				if (child.type == fsType.FILE) leafs.push(child);
				else traverse(child);
			});
		}
		if (currentNode.type == fsType.FILE) leafs.push(currentNode);
		else traverse(currentNode);
		if (!leafs.length) return [];
		return leafs.map(leaf => {
			return documentFromLeaf(leaf);
		});
	}

	// returns first node matching provided cmp function
	function findInTree(node, cmp) {
		if (cmp(node)) return node;
		else {
			for (let child of node.children) {
				const result = findInTree(child, cmp);
				if (result) return result;
			}
			return null;
		}
	}

	function documentFromLeaf(leaf) {
		const hierarchy = constructPath(leaf);
		return {
			name: leaf.name,
			path: hierarchy,
			id: leaf.id,
			confidential: leaf.confidential,
			proj_id: projectId,
			is_main: leaf.isMainDoc
		}
	}

	function constructPath(node) {
		const hierarchy = [];
		let currentNode = node;
		while (currentNode.parent) {
			currentNode = currentNode.parent;
			hierarchy.push(currentNode.name);
		}
		hierarchy.pop();
		hierarchy.reverse();
		return hierarchy;
	}


	function handleMessage(event) {
		const detail = event.detail;
		const parent = detail.parent;
		const currentName = detail.name;
		let current = documents;
		if (parent) current = parent.children.find(child => child.name == currentName);
		if (detail.what == 'editName') {
			const oldName = current.name;
			// names.delete(oldName);
			current.editing = false;
			current.name = newName(detail.newName, parent);
			updateDocs(getAffectedLeafs(current)).then(res => {
				if (!res) {
					current.name = newName(oldName, parent); 
					documents = documents
				}
			});
		}
		else if (detail.what == 'setConfidential') {
			current.confidential = detail.confidential;
			updateDocs([documentFromLeaf(current)]);
		}
		else if (detail.what == 'setMain') {
			const currentMain = findInTree(documents, node => node.isMainDoc == true);
			current.isMainDoc = detail.main;
			updateDocs([documentFromLeaf(current)]);
			if (currentMain) {
				currentMain.isMainDoc = false;
				updateDocs([documentFromLeaf(currentMain)]);
			}
		}
		else if (detail.what == 'addFolder') {
			const newFolder = newNode(detail.folderName, current, fsType.FOLDER);
			newFolder.editing = true;
			current.children.push(newFolder);
		}
		else if (detail.what == 'addFile') {
			const addedNode = newNode(detail.file.name, current, fsType.FILE, null, false);
			addDoc(addedNode, detail.file).then(res => {
				if (res) current.children.unshift(addedNode);
				documents = documents;
				if ( res == 'image_failed') window.alert('Document added, but image extraction failed');
			});
		}
		else if (detail.what == 'deleteNode') {
			removeDocs(getAffectedLeafs(current)).then(res => {
				if (res)  { 
					getAffectedLeafs(current);
					const indexRemoved = parent.children.findIndex(child => child.name == currentName);
					parent.children.splice(indexRemoved, 1);
					documents = documents;
				}
			});
		}
		documents = documents; // trigger reactiveness
	}

	function updateDocs(newDocs) {
		if (!newDocs.length) return Promise.resolve(true);
		const [headers, data] = preparePost({
			docs: JSON.stringify(newDocs)
		});
		return fetch(`${appPrefix}/update_documents/`, {
			method: 'POST', 
			headers: headers,
			body: data
		}).then( res => {
			if (!res.ok) { throw res }
			return true;
		}).catch( err => {
			err.text().then( errorMessage => console.log(errorMessage) );
			return false;
		});
	}

	function removeDocs(docs) {
		if (!docs.length) return Promise.resolve(true);
		const [headers, data] = preparePost({
			doc_ids: JSON.stringify(docs.map(doc => doc.id))
		});
		return fetch(`${appPrefix}/delete_documents/`, {
			method: 'POST', 
			headers: headers,
			body: data
		}).then( res => {
			if (!res.ok) { throw res }
			return true;
		}).catch( err => {
			err.text().then( errorMessage => console.log(errorMessage) );
			return false;
		});
	}
	
	function addDoc(addedNode, file) {
		const [headers, data] = preparePost({
			file: file,
			proj_id: projectId,
			path: JSON.stringify(constructPath(addedNode)),
			name: addedNode.name
		});
		return fetch(`${appPrefix}/add_document/`, {
			method: 'POST',
			headers: headers,
			body: data
		}).then( res => {
			if (!res.ok) { throw res }
			return res.json()
		}).then(json => {
			addedNode.id = json.id;
			if(json.image) {
				document.getElementById('project-img').setAttribute('src', json.image);
				projectImage = json.image;
			}
			else if (json.image == false) return 'image_failed';
			return true;
		}).catch( err => {
			err.text().then( errorMessage => console.log(errorMessage) );
			return false;
		});
	}

	function deleteThumbnail() {
		if (!confirm('Delete the project thumbnail image?')) return;
		const [headers, data] = preparePost({ proj_id: projectId });
		fetch(`${appPrefix}/delete_thumbnail/`, {
			method: 'POST',
			headers: headers,
			body: data
		}).then(res => {
			if (!res.ok) { throw res }
			return res.json();
		}).then(json => {
			if (json.success) {
				projectImage = null;
				const imgEl = document.getElementById('project-img');
				if (imgEl) imgEl.removeAttribute('src');
			}
		}).catch(err => {
			console.log('Error deleting thumbnail');
		});
	}

</script>

<style>
	.project-thumbnail {
		display: flex;
		align-items: center;
		gap: 1rem;
		margin-bottom: 1rem;
		padding: 0.75rem;
		background: #f8f9fa;
		border: 1px solid #e9ecef;
		border-radius: 4px;
	}
	.project-thumbnail img {
		max-width: 80px;
		max-height: 80px;
		border-radius: 4px;
		object-fit: cover;
	}
	.project-thumbnail .thumbnail-info {
		flex: 1;
		font-size: 0.9rem;
		color: #666;
	}
	.project-thumbnail .thumbnail-info strong {
		color: #333;
	}
	.project-thumbnail .btn-delete-thumbnail {
		background: none;
		border: 1px solid #dc3545;
		color: #dc3545;
		padding: 4px 10px;
		border-radius: 4px;
		cursor: pointer;
		font-size: 0.85rem;
	}
	.project-thumbnail .btn-delete-thumbnail:hover {
		background: #dc3545;
		color: white;
	}
</style>

<span style="margin-bottom: 1rem; display: block;"> Confidential documents can only be seen by editors or admins </span>

{#if loading}
	<p style="color: #666; font-style: italic;">Loading documents...</p>
{:else if error}
	<p style="color: #dc3545;">Error loading documents: {error}</p>
{:else}
	{#if projectImage}
	<div class="project-thumbnail">
		<img src={projectImage} alt="Project thumbnail">
		<div class="thumbnail-info">
			<strong>Project thumbnail</strong><br>
			Auto-extracted from uploaded document
		</div>
		{#if !readonly}
		<button class="btn-delete-thumbnail" on:click={deleteThumbnail}>Delete</button>
		{/if}
	</div>
	{/if}

	{#if documents}
		<Node {...documents} on:message={handleMessage}/>
	{/if}
{/if}

